import { useEffect } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import {
  DescriptionOutlined,
  LaunchOutlined,
  SwapHorizOutlined,
} from "@mui/icons-material";
import { format } from "date-fns";

import NoContentCard from "src/reusable/Sidebar/NoContentCard";
import { useSourceViewerStore } from "../../stores/sourceViewerStore";
import { useUIStore } from "../../stores/UIStore";
import {
  AgentBadge,
  SourceClassificationChip,
} from "./SourceClassificationChip";
import { resolveContentPage } from "./buildDocumentAnnotations";
import { buildClaimClusters, buildSourceOpenTarget } from "./crossReferences";
import { mergeCitedWithUnavailable } from "./citedTierMerge";
import { TABULAR_DATASET_TYPE_IDS } from "src/reusable/Chat/citations/CitationPresentationContext";
import { formatAgentPath } from "./formatAgentPath";
import {
  groupReadByDocument,
  type ReadDocumentGroup,
} from "./readTierGrouping";
import type {
  AssistantResponseSources,
  ProvenanceExternalSource,
  ProvenanceSource,
  ProvenanceUnavailableSource,
} from "../../types/provenance";
import { externalProviderLabel } from "./ExternalSourceTier";
import { safeHref } from "./safeHref";
import { RecordHomeKind, recordHome, recordTitle } from "src/api/records";
import { revisionAttributionText } from "src/helpers/revisionPrefix";

type DocumentSourceTierProps = {
  sources: ProvenanceSource[];
  /**
   * "cited" renders at full emphasis; "read" is visually de-emphasized
   * (lighter, dimmed) to signal "reviewed but not cited".
   */
  variant: "cited" | "read";
  emptyTitle: string;
  emptyDescription: string;
  /**
   * Full message provenance (both tiers). Used on item click to gather ALL of
   * the clicked document's annotations (cited + read) for the viewer. When
   * absent, items render but aren't clickable-to-viewer.
   */
  provenance?: AssistantResponseSources;
  /**
   * This tier's unavailable entries (contract §document_unavailable): chunks
   * kept with safe metadata only. Cited ones render as inert "[n] Document
   * unavailable" rows at their stable citation_index; read ones as inert
   * unnumbered rows. The caller pre-filters by `cited` to match `variant`.
   */
  unavailable?: ProvenanceUnavailableSource[];
  /**
   * Cited EXTERNAL sources (ADAMS / OSTI / web) surfaced in the Cited tier
   * (#20423 A2). They carry a synthetic `cite_id` and a dialogue-global
   * `citation_index` (their inline [N]) but no chunk/SDR row, so they render as
   * their own numbered rows — anchored `source-N` for scroll-to, opening the
   * source url in a new tab. Cited variant only.
   */
  citedExternals?: ProvenanceExternalSource[];
  /**
   * Read EXTERNAL sources (#20423 web-read): non-cited web/ADAMS/OSTI results
   * the model consumed but didn't cite. Rendered as unnumbered link rows in the
   * Read tier (no `citation_index`, no `source-N` anchor). Read variant only.
   */
  readExternals?: ProvenanceExternalSource[];
};

/**
 * Resolve the project folder + SDR id for a source, used only to test
 * whether it's a project-file source at all (`canOpenSource`/`isProjectFile`
 * below) — the drawer itself resolves sdr id -> file name against the
 * folder's own listing, so `data.file_name` is never needed here.
 */
const resolveOpenTarget = (
  source: ProvenanceSource,
): { folderId: number; sdrId: number } | null => {
  const sdr = source.source_data_record;
  const sdrId = sdr?.id;
  const home = recordHome({
    dataset_fk: sdr?.dataset_fk,
    project_folder_fk: sdr?.project_folder_fk,
  });
  if (home?.kind !== RecordHomeKind.Folder || sdrId == null) return null;
  return { folderId: home.folderId, sdrId };
};

/**
 * Resolve the dataset-document route target for a non-project source
 * (org_dataset / external_library / adhoc_upload). These don't live in a
 * project folder; instead they're addressed by their dataset + SDR id on the
 * `/datasets/:dataset_id/document/:sdr` route. Returns null when the source has
 * no dataset_fk (e.g. a pure external web/ADAMS source with only a URL), which
 * stays a plain external link.
 */
const resolveDatasetDocTarget = (
  source: ProvenanceSource,
): { datasetId: number; sdrId: number } | null => {
  const sdr = source.source_data_record;
  const sdrId = sdr?.id;
  const home = recordHome({
    dataset_fk: sdr?.dataset_fk,
    project_folder_fk: sdr?.project_folder_fk,
  });
  if (home?.kind !== RecordHomeKind.Dataset || sdrId == null) return null;
  return { datasetId: home.datasetId, sdrId };
};

/**
 * Resolve the 1-indexed page the viewer should open to for a source — the page
 * carrying the most of the chunk's content (see {@link resolveContentPage}), so
 * a chunk that spans a cover/header page opens on the real cited prose, not the
 * cover. Returns null when the chunk carries no page (e.g. tabular/CSV), so the
 * viewer falls back to its default.
 */
const resolveOpenPage = (source: ProvenanceSource): number | null =>
  resolveContentPage(source) ?? null;

/**
 * Section / section title from chunk metadata, matching the hover tooltip's
 * source (spec §5.1 asks for "section/page" in the Read and Cited tiers). Any
 * leading "§" is stripped so we render it once.
 */
const resolveSection = (source: ProvenanceSource): string | null => {
  const meta = source.data_chunk?.chunk_metadata as
    | { section?: unknown; section_title?: unknown }
    | undefined;
  const raw = meta?.section ?? meta?.section_title;
  if (typeof raw !== "string") return null;
  const trimmed = raw.replace(/^§\s*/, "").trim();
  return trimmed.length ? trimmed : null;
};

/** Format a provenance timestamp ("MMM d, yyyy p"), or null if unparseable. */
const formatRetrievedAt = (ts?: string | null): string | null => {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : format(d, "MMM d, yyyy p");
};

/**
 * The row identifier (source_uid) for a tabular/CSV source, else null. Tabular
 * datasets store one SourceDataRecord per row, so source_uid disambiguates rows
 * that all share the dataset name. Non-tabular sources return null.
 */
const tabularRowUid = (source: ProvenanceSource): string | null => {
  const typeId = source.dataset?.dataset_type?.id;
  if (typeId == null || !TABULAR_DATASET_TYPE_IDS.includes(typeId)) return null;
  const uid = source.source_data_record?.source_uid;
  return uid ? uid : null;
};

const EXCERPT_MAX_CHARS = 160;

/** One-line teaser of the cited passage (S4·AC6); the full text lives in the
 * annotated viewer. */
const excerptText = (
  chunk: ProvenanceSource["data_chunk"] | undefined,
): string | null => {
  const raw = chunk?.chunk_data?.data;
  if (typeof raw !== "string") return null;
  const collapsed = raw.replace(/\s+/g, " ").trim();
  if (!collapsed) return null;
  return collapsed.length > EXCERPT_MAX_CHARS
    ? `${collapsed.slice(0, EXCERPT_MAX_CHARS).trimEnd()}…`
    : collapsed;
};

/**
 * Renders a tier of document-backed sources (Cited or Read) as compact
 * reference rows (chat-v2 mockup): a document icon, the title, a "Page · Rev."
 * detail line, a one-line cited excerpt, plus a classification chip. The FULL
 * chunk text intentionally lives in the click-to-open annotated document
 * viewer — so many sources stay visible at once rather than one tall preview
 * per card (#20423).
 *
 * The Read variant is dimmed to communicate the funnel hierarchy: these were
 * pulled into context but not cited in the final answer.
 */
const DocumentSourceTier = ({
  sources,
  variant,
  emptyTitle,
  emptyDescription,
  provenance,
  unavailable = [],
  citedExternals = [],
  readExternals = [],
}: DocumentSourceTierProps) => {
  const toggleSource = useSourceViewerStore((s) => s.toggleSource);
  // Which source the in-chat viewer currently has open, so the matching card
  // renders a selected state and a second click toggles it closed (chat-v2 spec).
  const viewerOpen = useSourceViewerStore((s) => s.open);
  const viewerSdrId = useSourceViewerStore((s) => s.sdrId);
  const viewerChunkId = useSourceViewerStore((s) => s.chunkId);

  // Citation superscripts scroll the Cited tier to the matching ``source-N``
  // card (CitationLink.scrollToSource → UIStore.scrollTargetSource). The cards
  // used to live inside SourcesChunkView, which owned this effect; now that the
  // tier renders compact rows itself, it owns the scroll-into-view too. Only the
  // Cited tier carries the ``source-N`` anchors, so the lookup no-ops elsewhere.
  const scrollTargetSource = useUIStore((s) => s.scrollTargetSource);
  const setScrollTargetSource = useUIStore((s) => s.setScrollTargetSource);
  useEffect(() => {
    if (variant !== "cited" || scrollTargetSource == null) return;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = document.getElementById(`source-${scrollTargetSource}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          setScrollTargetSource(null);
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [variant, scrollTargetSource, sources.length, setScrollTargetSource]);

  const externalsToShow = variant === "cited" ? citedExternals : readExternals;
  if (!sources.length && !unavailable.length && !externalsToShow.length) {
    return <NoContentCard title={emptyTitle} description={emptyDescription} />;
  }

  // Whether the clicked source can open in an annotated viewer at all: a
  // project file (in-app folder viewer) OR any dataset-backed document
  // (org_dataset / external_library / adhoc_upload) via the dataset-document
  // route. Pure external sources (URL only, no dataset_fk) stay inert here.
  const canOpenSource = (source: ProvenanceSource): boolean =>
    !!resolveOpenTarget(source) || !!resolveDatasetDocTarget(source);

  // Open the clicked source's document with ALL of that document's annotations
  // (every cited + read passage) so the viewer shows them together — not just
  // the clicked chunk. IN-CONTEXT, in the right-side drawer over the chat
  // (InChatDocumentViewer) — for either home, and regardless of whether this
  // pane is mounted inside the AA Projects workspace or the standalone chat
  // (a layover only does its job if it never swaps away the surface the click
  // came from). "Open full viewer" in the drawer's own footer, which navigates
  // in-tab, is the one deliberate escape hatch to the full-page viewer.
  const handleOpen = (source: ProvenanceSource) => {
    const target = buildSourceOpenTarget(source, provenance);
    if (target) {
      // toggleSource (not openSource): clicking the already-open source closes
      // the drawer (click/unclick), clicking a different one switches to it.
      // `provenance` rides along so the viewer can derive cross-references.
      toggleSource(target);
    }
  };

  // True when this source's document+chunk is the one the in-chat viewer has
  // open — drives the selected card state and the toggle-closed affordance.
  const isActiveInViewer = (source: ProvenanceSource): boolean =>
    viewerOpen &&
    viewerSdrId === source.source_data_record?.id &&
    viewerChunkId === (source.data_chunk?.id ?? null);

  // One compact reference row. ``cardNumber`` is the inline citation number for
  // the Cited tier (drives the badge + ``source-N`` anchor); null for Read.
  // ``readGroup`` (Read tier only) swaps the detail line for the per-document
  // passage counts.
  const renderCard = (
    source: ProvenanceSource,
    cardNumber: number | null,
    readGroup?: ReadDocumentGroup,
  ) => {
    const canOpen = !!provenance && canOpenSource(source);
    const active = canOpen && isActiveInViewer(source);
    // Citation superscripts scroll to ``source-N`` (the Cited tier), so only
    // Cited keeps that id namespace; Read uses a distinct prefix to avoid
    // shadowing the Cited anchors.
    const displayIndex =
      cardNumber != null ? cardNumber - 1 : sources.indexOf(source);
    const domId = `${variant === "cited" ? "source" : "read-source"}-${
      cardNumber ?? displayIndex + 1
    }`;
    const sdr = source.source_data_record;
    // Extracted title first, stored filename as fallback.
    const title = recordTitle(sdr) ?? source.dataset?.name ?? "Source document";
    // A badge surfacing a project-file source's origin (spec §5.3), not a
    // distinct affordance — its own click resolves to the same drawer open as
    // the row.
    const isProjectFile = canOpen && !!resolveOpenTarget(source);
    const page = resolveOpenPage(source);
    const retrievedAt = formatRetrievedAt(source.retrieved_at);
    // Both tiers lead with the passage's page/revision (spec §5.1 — the Read
    // tier is NOT exempt from these). Read rows then add the per-document
    // passage counts (mockup parity); Cited rows add the agent path that
    // fetched the passage.
    const passageCounts = readGroup
      ? `${readGroup.passagesRetrieved} passage${
          readGroup.passagesRetrieved === 1 ? "" : "s"
        } retrieved${
          readGroup.passagesCited > 0
            ? ` · ${readGroup.passagesCited} cited`
            : ""
        }`
      : null;
    const section = resolveSection(source);
    // Tabular/CSV rows have no page/section — every cited row would otherwise
    // share the dataset-name title with no way to tell them apart. The row's
    // source_uid is its identifier (see CitationPresentationContext), so surface
    // it as the within-document locator, mirroring "§section"/"Page N".
    const rowUid = tabularRowUid(source);
    // Card anatomy (chat-v2 mockup + spec §1.4/S4): line 2 = WHERE in the
    // document (row / section / page), line 3 = attribution (revision + who
    // retrieved it + the Read tier's passage counts), then a one-line cited
    // excerpt (S4·AC6) and the classification chip (§1.4 — "displayed in
    // panel and export"). Retrieval timestamps stay off the card (tooltip).
    const detailBits = [
      rowUid != null ? `Row ${rowUid}` : null,
      section ? `§${section}` : null,
      page != null ? `Page ${page}` : null,
    ].filter(Boolean) as string[];
    const attributionBits = [
      source.revision ? revisionAttributionText(source.revision) : null,
      formatAgentPath(source),
      passageCounts,
    ].filter(Boolean) as string[];
    const excerpt = variant === "cited" ? excerptText(source.data_chunk) : null;
    const notCited = readGroup != null && readGroup.passagesCited === 0;
    return (
      <Box
        key={source.data_chunk?.id ?? displayIndex}
        id={domId}
        data-testid={`aa-src-${variant}-item-${displayIndex}`}
        {...(canOpen
          ? {
              role: "button" as const,
              tabIndex: 0,
              "aria-pressed": active,
              onClick: () => handleOpen(source),
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleOpen(source);
                }
              },
            }
          : {})}
        sx={{
          px: 1,
          py: 0.75,
          ...(canOpen
            ? {
                cursor: "pointer",
                borderLeft: "3px solid",
                borderLeftColor: active ? "primary.main" : "transparent",
                backgroundColor: active ? "action.selected" : undefined,
                "&:hover": { backgroundColor: "action.hover" },
              }
            : undefined),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {cardNumber != null && (
            <Box
              aria-hidden
              sx={{
                flexShrink: 0,
                mt: "1px",
                width: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                fontSize: "0.7rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {cardNumber}
            </Box>
          )}
          <DescriptionOutlined
            sx={{
              fontSize: "1.1rem",
              color: "text.secondary",
              flexShrink: 0,
              mt: "1px",
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600 }}
              noWrap
              title={title}
            >
              {title}
            </Typography>
            {detailBits.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={`aa-src-${variant}-detail-${displayIndex}`}
                sx={{ display: "block" }}
                noWrap
              >
                {detailBits.join(" · ")}
              </Typography>
            )}
            {attributionBits.length > 0 && (
              <Typography
                variant="caption"
                data-testid={`aa-src-${variant}-attrib-${displayIndex}`}
                title={retrievedAt ? `Retrieved ${retrievedAt}` : undefined}
                sx={{
                  display: "block",
                  color: "text.disabled",
                  mt: 0.25,
                }}
                noWrap
              >
                {attributionBits.join(" · ")}
              </Typography>
            )}
            {excerpt && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={`aa-src-${variant}-excerpt-${displayIndex}`}
                title={excerpt}
                sx={{ display: "block", mt: 0.25, fontStyle: "italic" }}
                noWrap
              >
                “{excerpt}”
              </Typography>
            )}
            {(source.classification || isProjectFile || notCited) && (
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
                sx={{ mt: 0.25 }}
              >
                <SourceClassificationChip
                  classification={source.classification}
                />
                {isProjectFile && (
                  <Chip
                    label="Project Files"
                    size="small"
                    variant="outlined"
                    color="primary"
                    data-testid={`aa-src-${variant}-projectfiles-${displayIndex}`}
                    onClick={(e) => {
                      // Not a separate destination — clicking it opens the same
                      // drawer the row does; stop propagation so we don't open
                      // twice.
                      e.stopPropagation();
                      handleOpen(source);
                    }}
                    sx={{
                      height: "22px",
                      fontSize: "0.7rem",
                      cursor: "pointer",
                    }}
                  />
                )}
                {notCited && (
                  <Chip
                    label="not cited"
                    size="small"
                    variant="outlined"
                    data-testid={`aa-src-read-not-cited-${displayIndex}`}
                    sx={{
                      height: "22px",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      borderColor: "divider",
                    }}
                  />
                )}
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>
    );
  };

  // Inert "Document unavailable" row (contract §document_unavailable): the
  // entry keeps its citation slot but exposes no title and no click target —
  // deleted vs unauthorized is deliberately indistinguishable.
  const renderUnavailableRow = (
    entry: ProvenanceUnavailableSource,
    citationNumber: number | null,
    index: number,
  ) => (
    <Box
      key={`unavailable-${entry.chunk_id}`}
      data-testid={`aa-src-${variant}-unavailable-${index}`}
      sx={{ px: 1, py: 0.75, opacity: 0.6 }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {citationNumber != null && (
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: "action.disabledBackground",
              color: "text.secondary",
              fontSize: "0.7rem",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {citationNumber}
          </Box>
        )}
        <DescriptionOutlined
          sx={{ fontSize: "1.1rem", color: "text.disabled", flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          Document unavailable
        </Typography>
      </Stack>
    </Box>
  );

  // Cited EXTERNAL row (#20423 A2): numbered like a chunk citation (its
  // dialogue-global `citation_index`), anchored `source-N` so a superscript
  // click scrolls here. No chunk/SDR to open in the viewer — clicking opens the
  // sanitized source url in a new tab (backend admits http/https only).
  const renderExternalRow = (ext: ProvenanceExternalSource, index: number) => {
    const num = ext.citation_index ?? null;
    // Live-overlay externals bypass the backend URL sanitizer — re-check the
    // http(s) scheme before the URL can reach window.open.
    const url = safeHref(typeof ext.url === "string" ? ext.url : null) ?? null;
    const title = ext.title || ext.identifier || "External source";
    const open = () => {
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    };
    return (
      <Box
        key={`external-${ext.cite_id ?? index}`}
        id={num != null ? `source-${num}` : undefined}
        data-testid={`aa-src-${variant}-external-${index}`}
        {...(url
          ? {
              role: "button" as const,
              tabIndex: 0,
              onClick: open,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  open();
                }
              },
            }
          : {})}
        sx={{
          px: 1,
          py: 0.75,
          ...(url
            ? {
                cursor: "pointer",
                borderLeft: "3px solid transparent",
                "&:hover": { backgroundColor: "action.hover" },
              }
            : undefined),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {num != null && (
            <Box
              aria-hidden
              sx={{
                flexShrink: 0,
                mt: "1px",
                width: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                fontSize: "0.7rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {num}
            </Box>
          )}
          <LaunchOutlined
            sx={{
              fontSize: "1.1rem",
              color: "text.secondary",
              flexShrink: 0,
              mt: "1px",
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600 }}
              noWrap
              title={title}
            >
              {title}
            </Typography>
            {ext.identifier && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
                noWrap
              >
                {ext.identifier}
              </Typography>
            )}
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              alignItems="center"
              sx={{ mt: 0.25 }}
            >
              <Chip
                label={externalProviderLabel(ext.source_type)}
                size="small"
                variant="outlined"
                sx={{
                  height: "22px",
                  fontSize: "0.7rem",
                  borderColor: "divider",
                }}
              />
              <AgentBadge agent={ext.retrieved_by_agent} />
            </Stack>
          </Box>
        </Stack>
      </Box>
    );
  };

  // Read tier: one row PER DOCUMENT (mockup parity) with its passage counts
  // ("N passages retrieved · M cited" / a "not cited" pill). The group's first
  // read source is the click representative — opening it shows ALL of the
  // document's annotations anyway. Unavailable read entries render as inert
  // unnumbered rows after the documents.
  if (variant === "read") {
    const groups = groupReadByDocument(
      sources,
      provenance?.assistant_response_sources ?? [],
    );
    return (
      <Box data-testid="aa-src-read-list" sx={{ opacity: 0.78 }}>
        {groups.map((group) => renderCard(group.sources[0], null, group))}
        {unavailable.map((entry, i) => renderUnavailableRow(entry, null, i))}
        {/* Read externals (#20423 web-read): unnumbered web/ADAMS/OSTI results
            the model consumed but didn't cite, after the internal documents. */}
        {externalsToShow.map(renderExternalRow)}
      </Box>
    );
  }

  // Cited tier with unavailable entries: render the merged flat sequence so
  // every row sits at its stable citation_index (claim grouping would hide
  // where the gaps fall — stable [N] numbering wins in this degraded state).
  if (unavailable.length > 0) {
    const merged = mergeCitedWithUnavailable(sources, unavailable);
    return (
      <Box data-testid="aa-src-cited-list">
        {merged.map((entry, i) =>
          entry.kind === "source"
            ? renderCard(entry.source, entry.citationNumber)
            : renderUnavailableRow(entry.entry, entry.citationNumber, i),
        )}
        {externalsToShow.map(renderExternalRow)}
      </Box>
    );
  }

  // Cited tier (chat-v2 mockup): ONLY cross-reference claims — sources from
  // more than one document supporting the same claim — render as a bracketed
  // group under the claim's heading. Everything else is a flat card, so the
  // grouping itself is the cross-reference signal (no per-card "cross-
  // referenced with [N]" rows in the panel; those jump links live in the
  // document viewer).
  const clusters = buildClaimClusters(provenance);
  return (
    <Box data-testid="aa-src-cited-list">
      {clusters.map((cluster, ci) => {
        const cards = cluster.members.map((m) =>
          renderCard(m.source, m.citationNumber),
        );
        if (!cluster.isCrossReference) return cards;
        return (
          // A contained group card — soft fill + accent bracket — so the
          // cross-reference reads as ONE unit at a glance (mockup), not as
          // indented loose cards.
          <Box
            key={`claim-cluster-${ci}`}
            data-testid="aa-src-claim-cluster"
            data-cross-reference="true"
            sx={{
              my: 1,
              mx: 0.5,
              py: 0.5,
              bgcolor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
              borderLeft: "3px solid",
              borderLeftColor: "primary.main",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ px: 1, pt: 0.25, color: "text.secondary" }}
            >
              <SwapHorizOutlined
                sx={{ fontSize: "0.9rem", flexShrink: 0 }}
                titleAccess="Cross-referenced across documents"
              />
              {cluster.heading && (
                <Typography
                  variant="caption"
                  noWrap
                  title={cluster.heading}
                  sx={{ fontStyle: "italic" }}
                >
                  {cluster.heading}
                </Typography>
              )}
            </Stack>
            {cards}
          </Box>
        );
      })}
      {externalsToShow.map(renderExternalRow)}
    </Box>
  );
};

export default DocumentSourceTier;
